import React, { createContext, useContext, useEffect, useState } from "react";

const AffiliateTrackingContext = createContext({});

const STORAGE_KEY = "sds_affiliate_tracking";
const EXPIRY_DAYS = 30; // Affiliate cookie lasts 30 days

/**
 * Extract affiliate code from URL
 * Supports: ?aff=CODE, ?aff_id=CODE, ?affiliate=CODE
 */
function extractAffiliateCode(searchString) {
  if (!searchString) return null;

  const params = new URLSearchParams(searchString);

  // Check various affiliate parameter names
  const affParams = ["aff", "aff_id", "affiliate", "ref", "partner"];
  for (const param of affParams) {
    const value = params.get(param);
    if (value) {
      return value.toUpperCase().trim();
    }
  }

  return null;
}

/**
 * Get stored affiliate data from localStorage
 */
function getStoredAffiliateData() {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Check if data is not expired
      if (parsed.expiry && parsed.expiry > Date.now()) {
        return parsed;
      }
      // Clear expired data
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    console.error("Error reading affiliate tracking from storage:", e);
  }
  return null;
}

/**
 * Save affiliate data to localStorage
 */
function saveAffiliateData(data) {
  if (typeof window === "undefined") return;

  try {
    const storageData = {
      ...data,
      expiry: Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
  } catch (e) {
    console.error("Error saving affiliate tracking to storage:", e);
  }
}

/**
 * Provider component that captures and stores affiliate tracking
 */
export function AffiliateTrackingProvider({ children }) {
  const [affiliateData, setAffiliateData] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Get existing stored affiliate data
    const storedData = getStoredAffiliateData();

    // Extract affiliate code from current URL
    const urlAffiliateCode = extractAffiliateCode(window.location.search);

    // If we have a new affiliate code in URL, use it (last touch attribution)
    if (urlAffiliateCode) {
      const newData = {
        affiliateCode: urlAffiliateCode,
        landingPage: window.location.pathname,
        landingTime: new Date().toISOString(),
        referrer: document.referrer || null,
        utmSource: new URLSearchParams(window.location.search).get("utm_source"),
        utmMedium: new URLSearchParams(window.location.search).get("utm_medium"),
        utmCampaign: new URLSearchParams(window.location.search).get("utm_campaign"),
      };
      saveAffiliateData(newData);
      setAffiliateData(newData);
    } else if (storedData) {
      // Use existing stored data
      setAffiliateData(storedData);
    }

    setIsReady(true);
  }, []);

  // Listen for URL changes (for SPAs)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleUrlChange = () => {
      const urlAffiliateCode = extractAffiliateCode(window.location.search);
      if (urlAffiliateCode) {
        const newData = {
          affiliateCode: urlAffiliateCode,
          landingPage: window.location.pathname,
          landingTime: new Date().toISOString(),
          referrer: document.referrer || null,
          utmSource: new URLSearchParams(window.location.search).get("utm_source"),
          utmMedium: new URLSearchParams(window.location.search).get("utm_medium"),
          utmCampaign: new URLSearchParams(window.location.search).get("utm_campaign"),
        };
        saveAffiliateData(newData);
        setAffiliateData(newData);
      }
    };

    window.addEventListener("popstate", handleUrlChange);
    return () => window.removeEventListener("popstate", handleUrlChange);
  }, []);

  const value = {
    // Current affiliate data
    affiliateData,
    affiliateCode: affiliateData?.affiliateCode || null,
    isReady,

    // Check if visitor came from an affiliate
    hasAffiliate: () => !!affiliateData?.affiliateCode,

    // Get affiliate tracking data for form submissions
    getAffiliateTrackingData: () => {
      if (!affiliateData) return null;
      return {
        affiliateCode: affiliateData.affiliateCode,
        affiliateLandingPage: affiliateData.landingPage,
        affiliateLandingTime: affiliateData.landingTime,
        affiliateReferrer: affiliateData.referrer,
        affiliateUtmSource: affiliateData.utmSource,
        affiliateUtmMedium: affiliateData.utmMedium,
        affiliateUtmCampaign: affiliateData.utmCampaign,
      };
    },

    // Clear affiliate tracking (e.g., after conversion)
    clearAffiliateTracking: () => {
      if (typeof window !== "undefined") {
        localStorage.removeItem(STORAGE_KEY);
      }
      setAffiliateData(null);
    },
  };

  return (
    <AffiliateTrackingContext.Provider value={value}>
      {children}
    </AffiliateTrackingContext.Provider>
  );
}

/**
 * Hook to access affiliate tracking data
 */
export function useAffiliateTracking() {
  const context = useContext(AffiliateTrackingContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      affiliateData: null,
      affiliateCode: null,
      isReady: true,
      hasAffiliate: () => false,
      getAffiliateTrackingData: () => null,
      clearAffiliateTracking: () => {},
    };
  }
  return context;
}

export default AffiliateTrackingContext;
